import React, { useMemo } from "react";
import {
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiTitle,
  EuiText,
  EuiHealth,
  EuiSpacer,
  EuiHorizontalRule,
  EuiIcon,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiToolTip,
  EuiDescriptionList,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import {
  PrtgDevice,
  PrtgSensor,
  PrtgStatusCategory,
} from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { FormSkeleton } from "src/components/skeleton";
import { usePrtgStatus } from "../usePrtgStatus";
import {
  deriveHealth,
  MetricKind,
  METRIC_ICON,
  pickMetricSensors,
  alertingSensors,
} from "../health";

const CATEGORY_COLOR: Record<PrtgStatusCategory, string> = {
  up: "success",
  down: "danger",
  warning: "warning",
  paused: "subdued",
  unusual: "accent",
  unknown: "subdued",
};

const i18n = "anketa.opsanio.appliance.flyout.health";

/** Compact relative-time label, falling back to a localized absolute date. */
const useRelativeTime = () => {
  const { translate } = useAnketaI18n();
  return (iso: string | null): string => {
    if (!iso) return "-";
    const then = new Date(iso).getTime();
    if (isNaN(then)) return "-";
    const diffSec = Math.round((Date.now() - then) / 1000);
    if (diffSec < 60)
      return translate(`${i18n}.time.justNow`, { defaultMessage: "just now" });
    if (diffSec < 3600)
      return translate(`${i18n}.time.minutes`, {
        values: { count: Math.floor(diffSec / 60) },
        defaultMessage: "{{count}} min ago",
      });
    if (diffSec < 86400)
      return translate(`${i18n}.time.hours`, {
        values: { count: Math.floor(diffSec / 3600) },
        defaultMessage: "{{count}} h ago",
      });
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
};

const SectionTitle: React.FC<{ icon: string; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
    <EuiFlexItem grow={false}>
      <EuiIcon type={icon} size="m" color="subdued" />
    </EuiFlexItem>
    <EuiFlexItem grow={false}>
      <EuiTitle size="xxs">
        <h4>{children}</h4>
      </EuiTitle>
    </EuiFlexItem>
  </EuiFlexGroup>
);

export const OpsanioApplianceFlyoutHealth = ({
  appliance,
  onOpenMonitoring,
}: {
  appliance: OpsanioApplianceData;
  /** Switch the flyout to the full PRTG monitoring tab. */
  onOpenMonitoring?: () => void;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const relativeTime = useRelativeTime();
  const instanceName = appliance.common?.metadata?.name?.trim() ?? "";
  const { status, loading, error, refetch } = usePrtgStatus(instanceName);

  const health = useMemo(() => deriveHealth(status), [status]);
  const allSensors = useMemo<PrtgSensor[]>(
    () => (status?.devices ?? []).flatMap((d: PrtgDevice) => d.sensors),
    [status],
  );
  const metricSensors = useMemo(
    () => pickMetricSensors(allSensors),
    [allSensors],
  );
  const alerts = useMemo(() => alertingSensors(allSensors), [allSensors]);


  // Identity, status and the health snapshot live in the flyout header now,
  // so this view starts directly with the details.
  const wrap = (body: React.ReactNode) => <>{body}</>;

  // --- Non-success states (DESIGN.md: never render an empty container) -------

  if (instanceName === "") {
    return wrap(
      <EuiCallOut
        title={translate(`${i18n}.noInstance.title`, {
          defaultMessage: "Monitoring not available",
        })}
        color="warning"
        iconType="help"
      >
        <p>
          {translate(`${i18n}.noInstance.body`, {
            defaultMessage:
              "This appliance has no VPC name (common.metadata.name), so live PRTG monitoring cannot be matched to it.",
          })}
        </p>
      </EuiCallOut>,
    );
  }

  if (loading) {
    return wrap(<FormSkeleton />);
  }

  if (error) {
    return wrap(
      <EuiCallOut
        title={translate(`${i18n}.error.title`, {
          defaultMessage: "Monitoring data unavailable",
        })}
        color="danger"
        iconType="alert"
      >
        <p>
          {translate(`${i18n}.error.body`, {
            defaultMessage:
              "Live monitoring could not be reached. PRTG may be unreachable or not yet provisioned for this appliance.",
          })}
        </p>
        <EuiFlexGroup gutterSize="s" responsive={false} wrap>
          <EuiFlexItem grow={false}>
            <EuiButton size="s" color="danger" iconType="refresh" onClick={refetch}>
              {translate("anketa.common.retry", { defaultMessage: "Retry" })}
            </EuiButton>
          </EuiFlexItem>
          {onOpenMonitoring && (
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty size="s" iconType="visGauge" onClick={onOpenMonitoring}>
                {translate(`${i18n}.action.openMonitoring`, {
                  defaultMessage: "Open monitoring",
                })}
              </EuiButtonEmpty>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiCallOut>,
    );
  }

  if (!health.monitored) {
    return wrap(
      <EuiEmptyPrompt
        iconType="visGauge"
        title={
          <h3>
            {translate(`${i18n}.unmonitored.title`, {
              defaultMessage: "No monitoring configured",
            })}
          </h3>
        }
        body={
          <EuiText size="s" color="subdued">
            {translate(`${i18n}.unmonitored.body`, {
              defaultMessage:
                "No PRTG sensors are reporting for this appliance yet. Provision monitoring to see live health.",
            })}
          </EuiText>
        }
        actions={
          onOpenMonitoring ? (
            <EuiButton color="primary" fill iconType="visGauge" onClick={onOpenMonitoring}>
              {translate(`${i18n}.action.setupMonitoring`, {
                defaultMessage: "Set up monitoring",
              })}
            </EuiButton>
          ) : undefined
        }
      />,
    );
  }

  // --- Success ---------------------------------------------------------------

  const technicalDetails = [
    {
      title: translate(`${i18n}.meta.host`, { defaultMessage: "Host / IP" }),
      description:
        status?.devices
          ?.map((d: PrtgDevice) => d.host)
          .filter(Boolean)
          .join(", ") || "-",
    },
    {
      title: translate(`${i18n}.meta.devices`, { defaultMessage: "Devices" }),
      description: String(status?.devices?.length ?? 0),
    },
    {
      title: translate(`${i18n}.meta.group`, { defaultMessage: "PRTG group" }),
      description: status?.group || status?.rootGroup || "-",
    },
    {
      title: translate(`${i18n}.meta.lastSync`, { defaultMessage: "Last sync" }),
      description: relativeTime(health.lastSync),
    },
  ];

  const monitoringUrl =
    status?.baseUrl && status?.groupId
      ? `${status.baseUrl.replace(/\/+$/, "")}/group.htm?id=${status.groupId}`
      : status?.baseUrl
        ? status.baseUrl.replace(/\/+$/, "")
        : undefined;

  return wrap(
    <EuiFlexGroup direction="column" gutterSize="l">
      {/* 3. Live metrics (current values - no fabricated history) */}
      <EuiFlexItem>
        <SectionTitle icon="visGauge">
          {translate(`${i18n}.metrics.title`, {
            defaultMessage: "Live metrics (current)",
          })}
        </SectionTitle>
        <EuiSpacer size="s" />
        <MetricsRow metricSensors={metricSensors} />
      </EuiFlexItem>

      {/* 4. Alerts & incidents */}
      <EuiFlexItem>
        <SectionTitle icon="alert">
          {translate(`${i18n}.alerts.title`, {
            defaultMessage: "Alerts & incidents",
          })}
        </SectionTitle>
        <EuiSpacer size="s" />
        {alerts.length === 0 ? (
          <EuiCallOut
            size="s"
            color="success"
            iconType="checkInCircleFilled"
            title={translate(`${i18n}.alerts.none`, {
              defaultMessage: "No active incidents",
            })}
          />
        ) : (
          <EuiFlexGroup direction="column" gutterSize="s">
            {alerts.slice(0, 8).map((s) => (
              <EuiFlexItem key={s.objid}>
                <EuiPanel paddingSize="s" hasShadow={false} hasBorder>
                  <EuiFlexGroup
                    gutterSize="s"
                    alignItems="center"
                    responsive={false}
                  >
                    <EuiFlexItem grow={false}>
                      <EuiHealth color={CATEGORY_COLOR[s.category]} />
                    </EuiFlexItem>
                    <EuiFlexItem>
                      <EuiText size="s">
                        <strong>{s.sensor}</strong>
                      </EuiText>
                      <EuiText size="xs" color="subdued">
                        {s.device}
                        {s.message ? ` · ${s.message}` : ""}
                      </EuiText>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiText size="xs" color="subdued">
                        {s.lastValue || s.status}
                      </EuiText>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiPanel>
              </EuiFlexItem>
            ))}
            {alerts.length > 8 && (
              <EuiFlexItem grow={false}>
                <EuiText size="xs" color="subdued">
                  {translate(`${i18n}.alerts.more`, {
                    values: { count: alerts.length - 8 },
                    defaultMessage: "+{{count}} more in monitoring",
                  })}
                </EuiText>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        )}
      </EuiFlexItem>

      {/* 5. Technical metadata */}
      <EuiFlexItem>
        <SectionTitle icon="iInCircle">
          {translate(`${i18n}.meta.title`, {
            defaultMessage: "Technical metadata",
          })}
        </SectionTitle>
        <EuiSpacer size="s" />
        <EuiDescriptionList
          type="column"
          compressed
          textStyle="reverse"
          listItems={technicalDetails}
        />
      </EuiFlexItem>

      {/* 6. Actions */}
      <EuiFlexItem>
        <EuiHorizontalRule margin="xs" />
        <EuiFlexGroup gutterSize="s" responsive={false} wrap>
          <EuiFlexItem grow={false}>
            <EuiButton size="s" iconType="refresh" onClick={refetch}>
              {translate("anketa.opsanio.refresh", { defaultMessage: "Refresh" })}
            </EuiButton>
          </EuiFlexItem>
          {onOpenMonitoring && (
            <EuiFlexItem grow={false}>
              <EuiButton size="s" iconType="visGauge" onClick={onOpenMonitoring}>
                {translate(`${i18n}.action.allSensors`, {
                  defaultMessage: "All sensors",
                })}
              </EuiButton>
            </EuiFlexItem>
          )}
          {monitoringUrl && (
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                iconType="popout"
                href={monitoringUrl}
                target="_blank"
              >
                {translate(`${i18n}.action.openPrtg`, {
                  defaultMessage: "Open in PRTG",
                })}
              </EuiButton>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>,
  );
};

const MetricsRow: React.FC<{
  metricSensors: Partial<Record<MetricKind, PrtgSensor>>;
}> = ({ metricSensors }) => {
  const { translate } = useAnketaI18n();
  const order: MetricKind[] = ["cpu", "memory", "disk", "network"];
  const label: Record<MetricKind, string> = {
    cpu: translate(`${i18n}.metric.cpu`, { defaultMessage: "CPU" }),
    memory: translate(`${i18n}.metric.memory`, { defaultMessage: "Memory" }),
    disk: translate(`${i18n}.metric.disk`, { defaultMessage: "Disk" }),
    network: translate(`${i18n}.metric.network`, { defaultMessage: "Network" }),
  };
  const present = order.filter((k) => metricSensors[k]);

  if (present.length === 0) {
    return (
      <EuiText size="s" color="subdued">
        {translate(`${i18n}.metrics.none`, {
          defaultMessage:
            "No CPU / memory / disk sensors were identified for this appliance.",
        })}
      </EuiText>
    );
  }

  return (
    <EuiFlexGroup gutterSize="m" wrap>
      {present.map((kind) => {
        const sensor = metricSensors[kind]!;
        return (
          <EuiFlexItem key={kind} style={{ minWidth: 140 }}>
            <EuiPanel paddingSize="s" hasShadow={false} hasBorder>
              <EuiFlexGroup
                gutterSize="xs"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  <EuiIcon type={METRIC_ICON[kind]} size="m" color="subdued" />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="xs" color="subdued">
                    {label[kind]}
                  </EuiText>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiHealth color={CATEGORY_COLOR[sensor.category]} />
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size="xs" />
              <EuiToolTip content={sensor.sensor}>
                <EuiTitle size="xs">
                  <span>{sensor.lastValue || sensor.status || "-"}</span>
                </EuiTitle>
              </EuiToolTip>
            </EuiPanel>
          </EuiFlexItem>
        );
      })}
    </EuiFlexGroup>
  );
};

export default OpsanioApplianceFlyoutHealth;
